import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import pinPrompt from '../../utils/pinPrompt';
import { tmRequest } from '../../utils/tmApi';
import { promptMaxQty, promptQuestion, remakeLinkBody } from '../../utils/tmDefects';

const keyOf = (entry, p) => `${entry.job_id}:${p.defect_id}`;

/**
 * Per defect: the scrapped pieces not yet linked to a remake (open_qty). Every job of one batch is
 * asked about the same open defect with the full open_qty (each job's hints are read before any link
 * exists), so the answers for one defect share this budget. A defect without open_qty: no limit here.
 */
function defectBudgets(list) {
  const out = {};
  list.forEach((e) => (e.prompts || []).forEach((p) => {
    if (p.open_qty === null || p.open_qty === undefined) return;
    const open = Math.max(0, Math.floor(Number(p.open_qty) || 0));
    out[p.defect_id] = out[p.defect_id] === undefined ? open : Math.max(out[p.defect_id], open);
  }));
  return out;
}
const jobLabel = (entry) => (entry.master_job_num ? `Job #${entry.master_job_num}` : `Job id ${entry.job_id}`);

/**
 * After Job Entry loads jobs onto a Time & Materials order: what add_cut_sheet / add_cut_list said
 * about each T&M job (ui-plan.md §1.7).
 *  - overrun (tm_warnings): the customer is billed for every good piece; the API's own message.
 *  - "Is this a remake of defect #X (n pcs at station)?" (tm_remake_prompt): pre-checked when the
 *    API's heuristic matches; pieces = suggested_qty (never above open_qty), editable down to 1.
 *    [Save links] asks ONE plain PIN (any active user; a link is a fact, not a billing call) and
 *    POSTs tm_remake_links with source 'job_entry' per checked question, showing each result
 *    (the warning when the job will be treated as a PRODUCTION job, not a dedicated remake).
 *  - tm_hints_error: the hints could not be read; the job itself loaded fine.
 * Nothing here blocks: Close keeps every job as created.
 *
 * Props: show, salesOrderId (null: links cannot be saved from here), orderNumber,
 *        entries [{job_id, master_job_num, warnings, prompts, hintsError}], onClose.
 */
export default function TmJobLoadHintsModal({ show, salesOrderId, orderNumber, entries, onClose }) {
  const list = useMemo(() => (Array.isArray(entries) ? entries : []), [entries]);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const [pinError, setPinError] = useState('');

  const budgets = useMemo(() => defectBudgets(list), [list]);

  useEffect(() => {
    if (!show) return;
    const next = {};
    // Pre-check a matched question only while its defect has pieces left for it (in row order), so
    // two jobs of one batch never both come pre-checked for the same pieces (the 2nd would get 409).
    const left = { ...budgets };
    list.forEach((e) => (e.prompts || []).forEach((p) => {
      const max = promptMaxQty(p);
      const room = left[p.defect_id] === undefined ? max : Math.min(max, left[p.defect_id]);
      const checked = !!p.heuristic_match && room > 0;
      if (checked && left[p.defect_id] !== undefined) left[p.defect_id] -= room;
      next[keyOf(e, p)] = { checked, qty: String(checked ? room : max) };
    }));
    setAnswers(next);
    setResults({});
    setBusy(false);
    setPinError('');
  }, [show, list, budgets]);

  const rows = [];
  list.forEach((e) => (e.prompts || []).forEach((p) => rows.push({ entry: e, p, key: keyOf(e, p) })));
  const qtyOk = (row) => {
    const a = answers[row.key];
    if (!a) return false;
    const q = Number(a.qty);
    return /^\d+$/.test(String(a.qty).trim()) && Number.isInteger(q) && q >= 1 && q <= promptMaxQty(row.p);
  };
  // Pieces the checked answers give each defect (saved links included): never above its open_qty.
  const usedByDefect = {};
  rows.forEach((r) => {
    const a = answers[r.key];
    if (!a || !a.checked) return;
    const q = Number(a.qty);
    if (Number.isFinite(q) && q > 0) usedByDefect[r.p.defect_id] = (usedByDefect[r.p.defect_id] || 0) + q;
  });
  const overBudget = (row) => budgets[row.p.defect_id] !== undefined
    && (usedByDefect[row.p.defect_id] || 0) > budgets[row.p.defect_id];
  const rowOk = (row) => qtyOk(row) && !overBudget(row);
  const pending = rows.filter((r) => answers[r.key] && answers[r.key].checked && !(results[r.key] && results[r.key].ok));
  const canSave = !!salesOrderId && pending.length > 0 && pending.every(rowOk) && !busy;

  const setAnswer = (key, patch) => setAnswers((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const save = async () => {
    if (!canSave) return;
    setPinError('');
    const pin = await pinPrompt('Enter your PIN to save the remake links:');
    if (!pin) return;
    setBusy(true);
    for (const row of pending) {
      const qty = Number(answers[row.key].qty);
      const r = await tmRequest('POST', `/sales_orders/${salesOrderId}/tm_remake_links`, remakeLinkBody(row.p, qty, pin));
      if (r.status === 401) {
        // The same PIN would fail every other link too: stop, say so, keep the rest to retry.
        setPinError(r.text);
        break;
      }
      const res = r.ok
        ? { ok: true, text: `Linked: ${jobLabel(row.entry)} remakes ${qty} pc of defect #${row.p.defect_id}.`, warning: (r.data && r.data.warning) || null }
        : { ok: false, text: r.text };
      setResults((prev) => ({ ...prev, [row.key]: res }));
    }
    setBusy(false);
  };

  const close = () => { if (!busy && onClose) onClose(); };

  return (
    <Modal show={show} onHide={close} size="lg" centered backdrop="static" aria-labelledby="tm-load-hints-title">
      <Modal.Header closeButton={!busy}>
        <Modal.Title id="tm-load-hints-title" style={{ fontSize: '1.05rem' }}>
          <i className="bi bi-calculator me-1"></i>
          Time &amp; Materials{orderNumber ? ` ${orderNumber}` : ''}: check the jobs just loaded
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="small" data-testid="tm-load-hints">
        {list.map((e) => (
          <div key={e.job_id} className="mb-3">
            <div className="fw-semibold mb-1">{jobLabel(e)}</div>
            {e.hintsError && (
              <div className="alert alert-secondary py-1 mb-1" data-testid={`tm-hints-error-${e.job_id}`}>
                The T&amp;M checks could not be read for this job ({e.hintsError}). The job loaded fine; open the Sales Order to review it.
              </div>
            )}
            {(e.warnings || []).map((w, i) => (
              <div key={`w${i}`} className="alert alert-warning py-1 mb-1" data-testid={`tm-overrun-${e.job_id}-${i}`}>
                <i className="bi bi-exclamation-triangle me-1"></i>
                {w.message || `Line ${w.so_line_id}: ${w.extra} extra pcs over the order.`}
              </div>
            ))}
            {(e.prompts || []).map((p) => {
              const k = keyOf(e, p);
              const a = answers[k] || { checked: false, qty: '' };
              const res = results[k];
              const done = !!(res && res.ok);
              const max = promptMaxQty(p);
              const over = !!a.checked && !done && overBudget({ key: k, p });
              return (
                <div key={k} className="border rounded p-2 mb-1" data-testid={`tm-remake-prompt-${k}`}>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input mt-0"
                      id={`tm-remake-${k}`}
                      checked={!!a.checked}
                      disabled={done || busy}
                      onChange={(ev) => setAnswer(k, { checked: ev.target.checked })}
                    />
                    <label className="form-check-label" htmlFor={`tm-remake-${k}`}>{promptQuestion(p)}</label>
                    <span className="d-inline-flex align-items-center gap-1 ms-auto">
                      <span aria-hidden="true">pieces</span>
                      <input
                        id={`tm-remake-qty-${k}`}
                        aria-label={`Pieces for defect #${p.defect_id} on ${jobLabel(e)}`}
                        type="text"
                        inputMode="numeric"
                        className={`form-control form-control-sm ${a.checked && !done && !rowOk({ key: k, p }) ? 'is-invalid' : ''}`}
                        style={{ width: 70 }}
                        value={a.qty}
                        disabled={!a.checked || done || busy}
                        onChange={(ev) => setAnswer(k, { qty: ev.target.value })}
                      />
                      <span className="text-muted">of {max}</span>
                    </span>
                  </div>
                  {over && (
                    <div className="text-danger mt-1" data-testid={`tm-remake-over-${k}`}>
                      Defect #{p.defect_id} has {budgets[p.defect_id]} pc not yet linked; the checked questions for it add up to {usedByDefect[p.defect_id]}.
                    </div>
                  )}
                  <div aria-live="polite">
                    {res && (
                      <div className={res.ok ? 'text-success mt-1' : 'text-danger mt-1'} role={res.ok ? 'status' : 'alert'}>
                        {res.text}
                        {res.warning && <div className="text-warning-emphasis"><i className="bi bi-exclamation-triangle me-1"></i>{res.warning}</div>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {rows.length > 0 && !salesOrderId && (
          <div className="alert alert-secondary py-1 mb-0">Link remakes from the Sales Order&apos;s Approve T&amp;M Pricing window.</div>
        )}
        {pinError && <div className="alert alert-danger py-1 mb-0" role="alert">{pinError}</div>}
      </Modal.Body>
      <Modal.Footer>
        {rows.length > 0 && (
          <Button variant="primary" size="sm" onClick={save} disabled={!canSave}>
            {busy ? 'Saving…' : 'Save links'}
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={close} disabled={busy}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
